import { useState, useMemo } from "react";
import { ZoomIn } from "lucide-react";
import ZoomableMedia from '../../../../../VueQuestion/ZoomableMedia';

/** Décode un SVG data URL en markup brut. Retourne null si ce n'est pas un SVG data URL. */
const decodeSvgDataUrl = (src) => {
    if (!src || !src.startsWith('data:image/svg')) return null;
    try {
        // data:image/svg+xml,<encodedSVG>  ou  data:image/svg+xml;base64,<b64>
        const commaIdx = src.indexOf(',');
        if (commaIdx === -1) return null;
        const header = src.slice(0, commaIdx);
        const payload = src.slice(commaIdx + 1);
        if (header.includes('base64')) {
            // atob() retourne une string Latin-1 : chaque char = 1 octet.
            // Pour préserver les caractères Unicode (ℓ, Δ, etc.), on ré-interprète
            // ces octets comme UTF-8 via TextDecoder.
            const latin1 = atob(payload);
            const bytes = Uint8Array.from(latin1, c => c.charCodeAt(0));
            return new TextDecoder('utf-8').decode(bytes);
        }
        return decodeURIComponent(payload);
    } catch { return null; }
};

/** CSS variables pour les couleurs structurelles SVG selon le thème. */
const svgThemeVars = {
    dark:  { '--svg-fg': '#e2e8f0', '--svg-fg-dim': '#94a3b8', '--svg-bg': '#1e293b', color: '#e2e8f0' },
    light: { '--svg-fg': '#1e293b', '--svg-fg-dim': '#475569', '--svg-bg': '#ffffff', color: '#1e293b' },
};

/**
 * Composant pour afficher une image responsive côté élève (Quiz)
 * Les SVG data URL sont rendus inline pour permettre l'adaptation des couleurs au thème.
 * Les images classiques (PNG, JPG, URL) restent en <img> standard.
 *
 * @param {Object} config - Configuration de l'image (imageUrl, imageData, maxWidth, alignment, alt, caption)
 * @param {string} theme - 'dark' (défaut) ou 'light'
 */
const ImageElementEleve = ({ config, theme = 'dark' }) => {
    const [isLoading, setIsLoading] = useState(true);

    const {
        imageUrl = null,       // URL R2 (priorité)
        imageData = null,      // Base64 (fallback)
        alt = 'Image de la question',
        maxWidth = 'full',
        alignment = 'center',
        caption = '',
    } = config || {};

    // Source de l'image : URL R2 en priorité, sinon base64
    const imageSrc = imageUrl || imageData;

    // Tenter de décoder le SVG — null si c'est une image classique
    const svgMarkup = useMemo(() => decodeSvgDataUrl(imageSrc), [imageSrc]);
    const isInlineSvg = svgMarkup !== null;

    // Un SVG "thémable" suit la convention currentColor / var(--svg-*) : ses
    // traits héritent du thème et restent lisibles sur fond sombre. Un SVG à
    // couleurs fixes (noir en dur — gen_664, legacy…) serait noir sur noir en
    // thème dark → rendu "papier" forcé : fond blanc + variables light, quel
    // que soit le thème (le zoom suit via --svg-bg, cf. ZoomableMedia).
    const isSvgThemable = useMemo(
        () => svgMarkup !== null
            && (svgMarkup.includes('currentColor') || svgMarkup.includes('var(--svg-')),
        [svgMarkup]
    );
    const svgStyle = isInlineSvg
        ? (isSvgThemable ? (svgThemeVars[theme] || svgThemeVars.dark) : svgThemeVars.light)
        : undefined;

    if (!imageSrc) return null;

    const isCompact = maxWidth === 'compact';

    /**
     * COMPACT = un choix de QCM (cf. ChoixRenderer). Le dessin garde sa TAILLE
     * NATURELLE : l'aperçu prof doit montrer ce que l'élève verra, or l'écran
     * élève borne la hauteur sans jamais agrandir (ChoixImageEleve). Un SVG est
     * rendu responsive par sanitizeSvg, qui remplace sa largeur par 100 % : une
     * figure de 124 px était donc étirée jusqu'à 300 (constat prof 2026-09-10).
     * On borne ici le conteneur à la largeur d'origine, lue sur le SVG source.
     */
    const largeurNaturelle = (() => {
        if (!isCompact || !svgMarkup) return null;
        const w = svgMarkup.match(/<svg[^>]*\swidth="(\d+(?:\.\d+)?)"/);
        const vb = svgMarkup.match(/viewBox="\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)/);
        const large = w ? Number(w[1]) : (vb ? Number(vb[1]) : null);
        // seulement si c'est PLUS PETIT que la borne compacte du mobile : un dessin
        // plus large continue d'être réduit par les classes, jamais agrandi par nous
        return large && large < 250 ? large : null;
    })();

    const getSizeClass = () => {
        switch (maxWidth) {
            case 'compact': return 'max-w-[250px] sm:max-w-[300px]';
            case 'small': return 'max-w-[300px]';
            case 'medium': return 'max-w-[500px]';
            case 'large': return 'max-w-[700px]';
            default: return 'max-w-full';
        }
    };

    const getAlignmentClass = () => {
        switch (alignment) {
            case 'left': return 'mr-auto';
            case 'right': return 'ml-auto';
            default: return 'mx-auto';
        }
    };

    const borderClass = (isInlineSvg && !isSvgThemable) || theme === 'light'
        ? 'border-gray-200 bg-white shadow-gray-200/50'
        : 'border-slate-600 bg-slate-800/50 shadow-blue-500/10';

    return (
        <div className="w-full my-4">
            <figure
                className={`${getAlignmentClass()} ${getSizeClass()} ${isCompact ? 'w-fit' : 'w-full'}`}
                style={largeurNaturelle ? { maxWidth: `${largeurNaturelle}px` } : undefined}
            >
                {/* Container avec skeleton + hover overlay */}
                <div
                    className={`relative group rounded-xl overflow-hidden border shadow-lg ${borderClass}`}
                    style={svgStyle}
                >
                    {/* Skeleton de chargement (images classiques uniquement) */}
                    {!isInlineSvg && isLoading && (
                        <div className={`absolute inset-0 flex items-center justify-center z-10 ${theme === 'light' ? 'bg-slate-100/80' : 'bg-slate-800/80'}`}>
                            <div className="flex flex-col items-center gap-3">
                                <div className="w-10 h-10 border-3 border-slate-600 border-t-blue-500 rounded-full animate-spin" />
                                <span className="text-xs text-slate-400">Chargement...</span>
                            </div>
                        </div>
                    )}

                    {/* SVG inline (thémable) ou image classique */}
                    {isInlineSvg ? (
                        <ZoomableMedia
                            type="svg"
                            content={svgMarkup}
                            alt={alt}
                            className="w-full"
                            svgThemeStyle={svgStyle}
                        />
                    ) : (
                        <ZoomableMedia
                            type="image"
                            src={imageSrc}
                            alt={alt}
                            className={`${isCompact ? 'w-auto' : 'w-full'} transition-all duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                            // COMPACT = un choix de QCM (cf. ChoixRenderer) : l'image garde sa
                            // TAILLE NATURELLE, bornée par le conteneur. Avec `w-full`, une
                            // figure de 124 px était agrandie jusqu'à 300 : l'aperçu prof
                            // montrait plus gros que l'écran élève, qui lui borne la hauteur
                            // sans jamais agrandir (ChoixImageEleve). Un aperçu doit montrer
                            // ce que l'élève verra (constat prof 2026-09-10).
                            imgClassName={`${isCompact ? 'w-auto max-w-full' : 'w-full'} h-auto object-contain ${isCompact ? 'max-h-[150px]' : 'max-h-[50vh]'}`}
                            onLoad={() => setIsLoading(false)}
                            onError={() => setIsLoading(false)}
                        />
                    )}

                    {/* Overlay au hover avec icône zoom */}
                    {(isInlineSvg || !isLoading) && (
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4 pointer-events-none">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/80 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                                <ZoomIn className="w-3.5 h-3.5" />
                                Agrandir
                            </div>
                        </div>
                    )}
                </div>

                {/* Légende */}
                {caption && (
                    <figcaption className={`text-center text-sm mt-3 italic ${theme === 'light' ? 'text-gray-500' : 'text-slate-400'}`}>
                        {caption}
                    </figcaption>
                )}
            </figure>
        </div>
    );
};

export default ImageElementEleve;
